import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { useLogEvent } from '~/hooks/useLogEvent'
import { useTamRatio } from '~/hooks/useTamRatio'
import {
  findByPath,
  type FiveNumber,
  fiveNumbers,
  type FiveSegment,
  fiveSegmentLabelI18nKeyMap,
  fiveToNineNumbersMap,
  NumFormat,
} from '~/utils'

import { dataDisplayTypeAtom } from '../../atoms'
import { useTabulationData } from '../../hooks/useTabulationQuery'

import { Arrows, fiveSegmentArrowRoutes, type FiveSegmentRoute } from './Arrows'
import styles from './FiveSegmentPanel.css'

export { fiveSegmentArrowRoutes }
export type { FiveSegmentRoute }

const segmentHeight = 50
const segmentGap = 2
const pyramidTopY = 5
const pyramidCenterX = 140
const bottomHalfWidth = 140

const labelX = pyramidCenterX + bottomHalfWidth
const svgWidth = labelX + 120
const svgHeight =
  pyramidTopY +
  fiveNumbers.length * segmentHeight +
  segmentGap * (fiveNumbers.length - 1) +
  5

const segmentFillColors: Record<FiveNumber, string> = {
  1: '#20b1c5',
  2: '#cff2f7',
  3: '#f2f2f2',
  4: '#f2f2f2',
  5: '#ffffff',
}

const getSegmentY = (fiveNumber: FiveNumber): number =>
  pyramidTopY + (fiveNumber - 1) * (segmentHeight + segmentGap)

const getSegmentCenterY = (fiveNumber: FiveNumber): number =>
  getSegmentY(fiveNumber) + segmentHeight / 2

const getSegmentPoints = (fiveNumber: FiveNumber): string => {
  const k = fiveNumber - 1
  const yTop = getSegmentY(fiveNumber)
  const yBot = yTop + segmentHeight
  const progressTop = k / fiveNumbers.length
  const progressBot = (k + 1) / fiveNumbers.length
  const halfWidthTop = bottomHalfWidth * progressTop
  const halfWidthBot = bottomHalfWidth * progressBot

  if (halfWidthTop === 0) {
    return [
      `${pyramidCenterX},${yTop}`,
      `${pyramidCenterX + halfWidthBot},${yBot}`,
      `${pyramidCenterX - halfWidthBot},${yBot}`,
    ].join(' ')
  }

  return [
    `${pyramidCenterX - halfWidthTop},${yTop}`,
    `${pyramidCenterX + halfWidthTop},${yTop}`,
    `${pyramidCenterX + halfWidthBot},${yBot}`,
    `${pyramidCenterX - halfWidthBot},${yBot}`,
  ].join(' ')
}

export type FiveSegmentPanelProps = {
  activeRoute: FiveSegmentRoute | null
  onRouteChange: (route: FiveSegmentRoute | null) => void
}

export const FiveSegmentPanel = ({
  activeRoute,
  onRouteChange,
}: FiveSegmentPanelProps) => {
  const { t } = useTranslation()
  const clipId = React.useId()

  const tabulationData = useTabulationData()
  const tamRatio = useTamRatio()
  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)

  const getFiveSegmentValue = (fiveNumber: FiveNumber): string => {
    const nineNums = fiveToNineNumbersMap[fiveNumber]
    const count = nineNums.reduce((sum, n) => {
      const item = R.pipe(tabulationData, findByPath('nineNumber', n))

      return sum + (item?.count ?? NaN)
    }, 0)
    const ratio = nineNums.reduce((sum, n) => {
      const item = R.pipe(tabulationData, findByPath('nineNumber', n))

      return sum + (item?.ratio ?? NaN)
    }, 0)

    return NumFormat.forCount(count, ratio, tamRatio, dataDisplayType)
  }

  const isCellInRoute = (fiveNumber: FiveNumber): boolean => {
    if (!activeRoute) {
      return false
    }

    return (
      activeRoute.fromSegmentNumber === fiveNumber ||
      activeRoute.toSegmentNumber === fiveNumber
    )
  }

  const logEvent = useLogEvent()
  const handleRouteClick = (segmentRoute: FiveSegmentRoute) => {
    onRouteChange(segmentRoute)
    logEvent('Select growth route', { withSuffix: true })
  }

  return (
    <svg
      className={styles.root}
      height={svgHeight}
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      width={svgWidth}
    >
      <defs>
        {fiveNumbers.map((fiveNumber) => (
          <clipPath
            key={fiveNumber}
            id={`${clipId}-segment-clip-${fiveNumber}`}
          >
            <polygon points={getSegmentPoints(fiveNumber)} />
          </clipPath>
        ))}
      </defs>

      {fiveNumbers.map((fiveNumber) => {
        const isActive = isCellInRoute(fiveNumber)

        return (
          <polygon
            key={fiveNumber}
            clipPath={`url(#${clipId}-segment-clip-${fiveNumber})`}
            fill={segmentFillColors[fiveNumber]}
            points={getSegmentPoints(fiveNumber)}
            stroke={
              isActive ? 'var(--colorAppPrimary)' : 'var(--colorBorderLight)'
            }
            strokeWidth={isActive ? 4 : 2}
          />
        )
      })}

      <Arrows
        activeRoute={activeRoute}
        centerX={pyramidCenterX}
        getSegmentCenterY={getSegmentCenterY}
        segmentHeight={segmentHeight}
        onRouteClick={handleRouteClick}
      />

      {fiveNumbers.map((fiveNumber) => {
        const centerY = getSegmentCenterY(fiveNumber)
        const key: FiveSegment = `fiveSegment${fiveNumber}`
        const isActive = isCellInRoute(fiveNumber)

        return (
          <React.Fragment key={fiveNumber}>
            <text
              className={styles.labelText}
              dominantBaseline="central"
              style={{
                color:
                  !activeRoute || isActive
                    ? 'var(--colorFontNormal)'
                    : 'var(--colorFontLight)',
                fill: 'currentColor',
              }}
              x={labelX}
              y={centerY}
            >
              {t(fiveSegmentLabelI18nKeyMap[key])}
            </text>

            <text
              className={styles.valueText}
              dominantBaseline="central"
              textAnchor="middle"
              x={pyramidCenterX}
              y={centerY}
            >
              {getFiveSegmentValue(fiveNumber)}
            </text>
          </React.Fragment>
        )
      })}
    </svg>
  )
}
